import React, { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { resubmitPrescription, resubmitClaim, uploadReimbursementFile } from '../../../Services/APIs/reimbursement';
import { FileField } from './formFields';
import StoredFile from './StoredFile';
import { UPLOAD_FIELD, FILE_NAME_FIELD, acceptFor, fieldLabel } from './reimbursementStatus';

// Re-uploads exactly the documents a reviewer asked for — nothing else on the Reimbursement can
// change here. After it is sent the Reimbursement goes back to review.
const ReuploadForm = ({ reimbursement, onClose, onSuccess }) => {
    const request = reimbursement.document_request;
    const isTreatment = request.stage === 'treatment';
    const source = isTreatment ? (reimbursement.treatment_proof || {}) : reimbursement;
    const uploadType = isTreatment ? 'claim' : 'prescription';

    const [uploads, setUploads] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isDone, setIsDone] = useState(false);

    // One upload slot per requested file (x-rays are two slots, at least one required).
    const slots = request.fields.flatMap((field) => {
        const upload = UPLOAD_FIELD[field];
        return Array.isArray(upload)
            ? upload.map((slot, index) => ({ field, slot, index, required: index === 0 }))
            : [{ field, slot: upload, index: 0, required: true }];
    });

    const setFile = async (slot, file) => {
        if (!file) {
            setUploads((prev) => { const next = { ...prev }; delete next[slot]; return next; });
            return;
        }
        setUploads((prev) => ({ ...prev, [slot]: { file, status: 'uploading', url: null, error: null } }));
        try {
            const response = await uploadReimbursementFile(file, { type: uploadType, field: slot });
            setUploads((prev) => ({ ...prev, [slot]: { file, status: 'done', url: response.data?.data?.url, error: null } }));
        } catch (err) {
            const message = err?.response?.data?.message || 'Upload failed. Please try again.';
            setUploads((prev) => ({ ...prev, [slot]: { file, status: 'error', url: null, error: message } }));
        }
    };

    const isAnyUploading = slots.some(({ slot }) => uploads[slot]?.status === 'uploading');

    const handleSubmit = async () => {
        if (isAnyUploading) {
            setError('Please wait for uploads to finish.');
            return;
        }
        const missing = slots.filter(({ slot, required }) => required && !uploads[slot]?.url);
        if (missing.length) {
            setError(`Please upload: ${[...new Set(missing.map(({ field }) => fieldLabel(field)))].join(', ')}.`);
            return;
        }

        const payload = {};
        request.fields.forEach((field) => {
            const upload = UPLOAD_FIELD[field];
            payload[field] = Array.isArray(upload)
                ? upload.map((slot) => uploads[slot]?.url).filter(Boolean)
                : uploads[upload].url;
            // Proof-of-treatment files also keep the name the member picked.
            if (isTreatment && FILE_NAME_FIELD[field]) payload[FILE_NAME_FIELD[field]] = uploads[upload]?.file?.name;
        });

        setIsSubmitting(true);
        setError('');
        try {
            if (isTreatment) {
                await resubmitClaim(reimbursement.current_claim_id || reimbursement.treatment_proof?._id, payload);
            } else {
                await resubmitPrescription(reimbursement._id, payload);
            }
            setIsDone(true);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to send. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isDone) {
        return (
            <div className="p-8 flex flex-col items-center text-center" data-testid="reupload-sent">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-gray-800 font-bold text-lg mb-1">Documents sent for review</h3>
                <p className="text-gray-400 text-sm mb-6">We'll review them again and update your reimbursement here.</p>
                <button onClick={onSuccess} className="w-full py-3 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors">
                    Done
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="bg-purple-600 p-5 rounded-t-2xl flex justify-between items-center flex-shrink-0">
                <div>
                    <h2 className="text-lg font-bold text-white">Re-upload documents</h2>
                    <p className="text-purple-100 text-xs mt-1">
                        {isTreatment ? 'Reimbursement treatment proof' : 'Reimbursement prescription'}
                    </p>
                </div>
                <button onClick={onClose} aria-label="Close" className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all">
                    <X className="w-4 h-4 text-white" />
                </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-5" data-testid="reupload-form">
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <p className="font-semibold">What our reviewer asked for</p>
                    <p className="mt-1" data-testid="reupload-note">“{request.note}”</p>
                </div>

                {error && <p role="alert" className="text-red-600 text-sm bg-red-50 border border-red-100 rounded-lg p-3">{error}</p>}

                {request.fields.map((field) => {
                    const stored = source[field];
                    const storedList = Array.isArray(stored) ? stored : stored ? [stored] : [];
                    return (
                        <div key={field} className="border border-gray-100 rounded-xl p-4 space-y-3">
                            <p className="text-sm font-bold text-gray-800">{fieldLabel(field)}</p>
                            {storedList.length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-500 mb-1">Current file</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {storedList.map((url, index) => (
                                            <StoredFile key={url} reimbursementId={reimbursement._id} field={field} index={index} storedUrl={url} label={fieldLabel(field)} />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {slots.filter((s) => s.field === field).map(({ slot, index, required }) => (
                                <FileField
                                    key={slot}
                                    label={Array.isArray(UPLOAD_FIELD[field]) ? `New file ${index + 1}${required ? '' : ' (optional)'}` : 'New file'}
                                    required={required}
                                    accept={acceptFor(slot)}
                                    testId={`reupload-${slot}`}
                                    file={uploads[slot]?.file}
                                    uploading={uploads[slot]?.status === 'uploading'}
                                    error={uploads[slot]?.error}
                                    onChange={(f) => setFile(slot, f)}
                                />
                            ))}
                        </div>
                    );
                })}

                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || isAnyUploading}
                    data-testid="reupload-submit"
                    className="w-full py-3 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors disabled:opacity-50"
                >
                    {isSubmitting ? 'Sending…' : isAnyUploading ? 'Uploading…' : 'Send for review'}
                </button>
            </div>
        </>
    );
};

export default ReuploadForm;
